import { useMutation } from "@tanstack/react-query";
import type { ScanResult } from "@wms/domain";
import { Alert, App, Button, Form, Input, InputNumber, List, Modal, Select, Space, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, describeError } from "../api/client";
import { useAuthStore } from "../auth/store";
import { RefSelect } from "./RefSelect";

interface Props {
  result: ScanResult;
  onClose: () => void;
}

/**
 * What the work screen shows when a scanned code is not a plain «open the card» case (task of 22.10, 4–5.2):
 * several objects share the code (choose one), the code is unknown (bind to a product, create a product, cancel),
 * the code is a malformed GS1 value or belongs to another warehouse (explain, change nothing).
 */
export function ScanResultModal({ result, onClose }: Props) {
  const navigate = useNavigate();
  const can = useAuthStore((s) => s.can);
  const { message } = App.useApp();
  const [mode, setMode] = useState<"menu" | "bind">("menu");
  const [form] = Form.useForm();
  const objectId = Form.useWatch("storage_object_id", form) as string | undefined;

  const bind = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.post<{ id: string; storage_object_id: string }>("/barcodes", {
        barcode: result.value,
        raw: result.raw,
        storage_object_id: values.storage_object_id,
        package_id: values.package_id ?? null,
        quantity: values.quantity ?? 1,
        source: values.source ?? "manufacturer",
      }),
    onSuccess: (row) => {
      message.success(`Код ${result.value} привязан к товару`);
      onClose();
      navigate(`/storage_objects/${row.storage_object_id}`);
    },
    onError: (e) => message.error(describeError(e), 6),
  });

  if (result.kind === "ambiguous") {
    return (
      <Modal open title="Код соответствует нескольким объектам" onCancel={onClose} footer={<Button onClick={onClose}>Отмена</Button>} destroyOnHidden>
        <Typography.Paragraph type="secondary">Выберите, что вы отсканировали. Остатки при этом не меняются.</Typography.Paragraph>
        <List
          dataSource={result.candidates}
          renderItem={(c) => (
            <List.Item
              actions={[
                <Button key="open" type="primary" onClick={() => { onClose(); navigate(c.path); }}>
                  Открыть
                </Button>,
              ]}
            >
              <List.Item.Meta title={c.label} description={c.hint ?? c.kind} />
            </List.Item>
          )}
        />
      </Modal>
    );
  }
  if (result.kind === "invalid" || result.kind === "forbidden") {
    return (
      <Modal open title={result.kind === "invalid" ? "Код не принят" : "Нет доступа"} onCancel={onClose} footer={<Button type="primary" onClick={onClose}>Понятно</Button>} destroyOnHidden>
        <Alert type={result.kind === "invalid" ? "error" : "warning"} showIcon message={result.reason} description={result.kind === "invalid" ? "Код заявлен как GS1, но не прошёл проверку. Повторный поиск как обычного кода не выполняется, чтобы не провести другой товар." : undefined} />
      </Modal>
    );
  }
  const canBind = can("nsi.barcodes.write");
  const canCreate = can("nsi.storage_objects.write");
  return (
    <Modal open title="Код не найден" onCancel={onClose} footer={null} destroyOnHidden>
      <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={result.reason} description="Пока код не привязан, никакие остатки по нему не создаются. Приёмка остаётся отдельным действием." />
      {mode === "menu" ? (
        <Space direction="vertical" style={{ width: "100%" }}>
          {canBind && (
            <Button type="primary" size="large" block onClick={() => setMode("bind")}>
              Привязать к существующему товару
            </Button>
          )}
          {canCreate && (
            <Button size="large" block onClick={() => { onClose(); navigate(`/storage_objects/new?pending_barcode=${encodeURIComponent(result.value)}`); }}>
              Создать новый товар
            </Button>
          )}
          {!canBind && !canCreate && <Typography.Text type="secondary">У вашей роли нет права привязывать коды или заводить товары: передайте код сотруднику с таким правом или руководителю.</Typography.Text>}
          <Button size="large" block onClick={onClose}>
            Отмена
          </Button>
        </Space>
      ) : (
        <Form form={form} layout="vertical" initialValues={{ quantity: 1, source: "manufacturer" }} onFinish={(v) => bind.mutate(v)}>
          <Form.Item label="Код" >
            <Input value={result.value} readOnly />
          </Form.Item>
          <Form.Item name="storage_object_id" label="Товар" rules={[{ required: true, message: "Выберите товар" }]}>
            <RefSelect entity="storage_objects" />
          </Form.Item>
          <Form.Item name="package_id" label="Уровень упаковки (пусто — штука)">
            <RefSelect entity="packages" filter={objectId ? { field: "storage_object_id", op: "eq", value: objectId } : null} placeholder={objectId ? "Начните вводить" : "Сначала выберите товар"} />
          </Form.Item>
          <Form.Item name="quantity" label="Сколько базовых единиц обозначает код">
            <InputNumber min={0.001} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="source" label="Чей это код">
            <Select options={[{ value: "manufacturer", label: "Производитель" }, { value: "supplier", label: "Поставщик" }, { value: "other", label: "Другой" }]} />
          </Form.Item>
          <Typography.Paragraph type="secondary">Перед сохранением проверьте: код → товар → упаковка → количество. Сохранение не принимает товар на склад.</Typography.Paragraph>
          <Space>
            <Button type="primary" htmlType="submit" loading={bind.isPending}>Сохранить связь</Button>
            <Button onClick={() => setMode("menu")}>Назад</Button>
          </Space>
        </Form>
      )}
    </Modal>
  );
}
